import { useRef, useState } from "react";
import { Status, StepDetails } from "types/stepper";

export const useStepper = <T>(intitialSteps: StepDetails<T>[]) => {
  const ref = useRef(null);

  const [steps, setSteps] = useState(intitialSteps);

  const { ACTIVE, VISITED, NOT_VISITED } = Status;

  const isFirstStep = steps[0].status === ACTIVE;
  const isLastStep = steps[steps.length - 1].status === ACTIVE;
  const [activeStep] = steps.filter((step) => step.status === ACTIVE);

  const scrollTop = () => {
    if (ref && ref.current) {
      (ref.current as HTMLElement).scrollIntoView({ behavior: "smooth" });
    }
  };

  const nextStep = () => {
    scrollTop();
    const activeStepIndex = steps.findIndex((step) => step.status === ACTIVE);
    if (activeStepIndex === steps.length - 1) return;

    return setSteps((prev) => {
      const nextStep = [...prev];

      nextStep[activeStepIndex].status = VISITED;
      nextStep[activeStepIndex + 1].status = ACTIVE;

      return nextStep;
    });
  };

  const skipToStep = (id: T) => {
    scrollTop();
    const currentActiveStepId = steps.findIndex(
      (step) => step.status === ACTIVE,
    );

    return setSteps(
      steps.map((step, idx) =>
        idx === currentActiveStepId
          ? {
              ...step,
              status: VISITED,
            }
          : {
              ...step,
              status: id === step.id ? ACTIVE : step.status,
            },
      ),
    );
  };

  const setStepsErrors = (stepsIds: Partial<T>[]) => {
    scrollTop();

    const stepsWithErrors = steps.map((step) => {
      if (stepsIds.includes(step.id)) {
        return {
          ...step,
          hasError: true,
        };
      } else {
        return { ...step, hasError: false };
      }
    });

    setSteps(stepsWithErrors);
  };

  const setStepError = (stepId: string, hasError: boolean) => {
    return setSteps(
      [...steps].map((step) => ({
        ...step,
        hasError: step.id === stepId ? hasError : step.hasError,
      })),
    );
  };

  const prevStep = () => {
    scrollTop();

    return setSteps((prevState) => {
      const activeStepIndex = prevState.findIndex(
        (step) => step.status === ACTIVE,
      );
      if (activeStepIndex === 0) return prevState;
      const nextStep = [...prevState];

      nextStep[activeStepIndex].status = VISITED;
      nextStep[activeStepIndex - 1].status = ACTIVE;

      return nextStep;
    });
  };

  const backToFirstStep = () => {
    scrollTop();

    return setSteps(
      steps.map((step, idx) => ({
        ...step,
        status: idx === 0 ? ACTIVE : NOT_VISITED,
      })),
    );
  };

  return {
    ref,
    steps,
    isLastStep,
    activeStep,
    isFirstStep,
    setSteps,
    nextStep,
    skipToStep,
    setStepError,
    setStepsErrors,
    prevStep,
    backToFirstStep,
  };
};
